import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiBookOpen, FiFilter } from 'react-icons/fi';
import { getApiUrl } from '../utils/api';
import TemplateCard, { TemplateSummary } from '../components/strategy/TemplateCard';

type ComplexityFilter = 'all' | 'beginner' | 'intermediate' | 'advanced';
type GpuFilter = 'any' | 'gpu' | 'cpu';

export default function StrategyLibraryPage() {
  const [templates, setTemplates] = useState<TemplateSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cloningSlug, setCloningSlug] = useState<string | null>(null);

  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [complexityFilter, setComplexityFilter] = useState<ComplexityFilter>('all');
  const [gpuFilter, setGpuFilter] = useState<GpuFilter>('any');

  const navigate = useNavigate();

  const fetchTemplates = useCallback(async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      navigate('/login');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${getApiUrl()}/api/v1/strategy-templates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        throw new Error(`Request failed: ${response.status}`);
      }
      const data = (await response.json()) as TemplateSummary[];
      setTemplates(data);
    } catch (e) {
      console.error('Failed to load strategy templates:', e);
      setError('Could not load strategy templates. Try again in a moment.');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const categories = useMemo(() => {
    const set = new Set<string>();
    templates.forEach((t) => set.add(t.category));
    return Array.from(set).sort();
  }, [templates]);

  const filtered = useMemo(() => {
    return templates.filter((t) => {
      if (categoryFilter !== 'all' && t.category !== categoryFilter) return false;
      if (complexityFilter !== 'all' && t.complexity !== complexityFilter) return false;
      if (gpuFilter === 'gpu' && !t.gpu_required) return false;
      if (gpuFilter === 'cpu' && t.gpu_required) return false;
      return true;
    });
  }, [templates, categoryFilter, complexityFilter, gpuFilter]);

  const handleClone = async (slug: string) => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      navigate('/login');
      return;
    }
    setCloningSlug(slug);
    try {
      const response = await fetch(
        `${getApiUrl()}/api/v1/strategy-templates/${slug}/clone`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.detail || `Clone failed (${response.status})`);
      }
      const result = await response.json();
      // Mirror the sample-notebook open flow: stash the destination path so
      // the Notebooks page can open it on arrival. See propalgos-file-explorer
      // skill for the broader pattern.
      sessionStorage.setItem(
        'strategy_template_clone',
        JSON.stringify({
          slug,
          destination: result.destination_notebook_path,
          opened_notebook_slug: result.opened_notebook_slug,
        }),
      );
      navigate('/dashboard/notebooks');
    } catch (e) {
      console.error('Clone failed:', e);
      setError(e instanceof Error ? e.message : 'Clone failed');
    } finally {
      setCloningSlug(null);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="mx-auto max-w-7xl px-6 py-10">
        <header className="mb-8">
          <div className="flex items-center gap-3 text-neutral-400">
            <FiBookOpen className="h-5 w-5" />
            <span className="text-sm uppercase tracking-wide">Research Library</span>
          </div>
          <h1 className="mt-2 text-3xl font-semibold">Strategy Templates</h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-neutral-400">
            Paper-backed Jupyter templates curated from quant research newsletters.
            Every template cites the sources that motivated it. Clone one into your
            workspace to run, customize, and backtest it on your own data.
          </p>
        </header>

        <section className="mb-6 rounded-lg border border-neutral-900 bg-neutral-950 p-4">
          <div className="flex items-center gap-3 text-sm text-neutral-400">
            <FiFilter className="h-4 w-4" />
            <span className="font-medium text-neutral-300">Filters</span>
          </div>
          <div className="mt-3 flex flex-wrap gap-3">
            <FilterSelect
              label="Category"
              value={categoryFilter}
              onChange={setCategoryFilter}
              options={[
                { value: 'all', label: 'All categories' },
                ...categories.map((c) => ({ value: c, label: c.replace(/_/g, ' ') })),
              ]}
            />
            <FilterSelect
              label="Complexity"
              value={complexityFilter}
              onChange={(v) => setComplexityFilter(v as ComplexityFilter)}
              options={[
                { value: 'all', label: 'Any' },
                { value: 'beginner', label: 'Beginner' },
                { value: 'intermediate', label: 'Intermediate' },
                { value: 'advanced', label: 'Advanced' },
              ]}
            />
            <FilterSelect
              label="Compute"
              value={gpuFilter}
              onChange={(v) => setGpuFilter(v as GpuFilter)}
              options={[
                { value: 'any', label: 'Any' },
                { value: 'gpu', label: 'GPU required' },
                { value: 'cpu', label: 'CPU only' },
              ]}
            />
          </div>
        </section>

        {error && (
          <div className="mb-6 rounded-md border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-white" />
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState hasFilters={
            categoryFilter !== 'all' || complexityFilter !== 'all' || gpuFilter !== 'any'
          } />
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {filtered.map((t) => (
              <TemplateCard
                key={t.public_id}
                template={t}
                onClone={handleClone}
                cloning={cloningSlug === t.slug}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <label className="flex flex-col text-xs text-neutral-500">
      <span className="mb-1 uppercase tracking-wide">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-md border border-neutral-800 bg-neutral-900 px-3 py-1.5 text-sm text-white focus:border-neutral-600 focus:outline-none"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function EmptyState({ hasFilters }: { hasFilters: boolean }) {
  return (
    <div className="rounded-lg border border-dashed border-neutral-800 bg-neutral-950 py-16 text-center">
      <FiBookOpen className="mx-auto h-8 w-8 text-neutral-600" />
      <h2 className="mt-4 text-lg font-medium text-neutral-200">
        {hasFilters ? 'No templates match these filters' : 'No templates yet'}
      </h2>
      <p className="mt-2 text-sm text-neutral-500">
        {hasFilters
          ? 'Loosen the filters or check back as new research rolls in.'
          : 'The library fills up as curated research lands from the weekly pipeline.'}
      </p>
    </div>
  );
}
